import { computed, ref } from 'vue'
import { backendApi } from '@/services/backendApiService'

/**
 * Which PMS the backend runs, and whether inventory / rates / tax codes are
 * managed in our own database (`capabilities.masterData`).
 *
 * The property-setup tabs branch on `native`: true → native create/edit
 * forms against /api/pms; false → the MEWS import modals every deployment
 * had before. One fetch per session, shared by every caller.
 *
 * Fail-safe: until the answer arrives, and whenever it can't be fetched
 * (older backend without /api/pms, network), `native` is false — the MEWS
 * UI is the known-good fallback and must never flash native controls on a
 * MEWS deployment.
 */

export interface PmsInfo {
  provider: string
  capabilities: Record<string, boolean>
}

const provider = ref<string>('unknown')
const capabilities = ref<Record<string, boolean>>({})
const loaded = ref(false)
let inflight: Promise<void> | null = null

async function load(): Promise<void> {
  try {
    const info = await backendApi.getPmsInfo()
    provider.value = info?.provider || 'unknown'
    capabilities.value = info?.capabilities || {}
  } catch {
    provider.value = 'unknown'
    capabilities.value = {}
  } finally {
    loaded.value = true
  }
}

export function usePmsInfo() {
  if (!inflight) inflight = load()
  return {
    provider,
    capabilities,
    loaded,
    native: computed(() => capabilities.value.masterData === true),
    ready: inflight,
  }
}
